//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React, { useState } from 'react';

import { DefaultIconButton } from '@components/atoms/button/Button';
import { recordEvent } from '@tracker/tracker';
import { IEventCategoryAndAction } from '@tracker/trackerModel';
import { isDefined } from '@utils/filterPredicates';
import { saveBlob } from '@utils/saveBlob';
import { ITrackableComponent } from '@utils/withTracking';

interface Props
  extends React.HTMLAttributes<HTMLButtonElement>,
    ITrackableComponent {
  onClick: () => Promise<BlobPart | undefined>;
  className?: string;
  fileName?: string;
  gaEventConfig?: IEventCategoryAndAction;
  headers?: [string];
  isDisabled?: boolean;
  text?: string;
}

/**
 * The DownloadButton atom renders a download icon with
 * optional text. It accepts GA event configuration, headers,
 * and fileName to be downloaded.
 *
 * Note that this fetches and downloads the file locally, whereas the
 * `DownloadToEmailButton` creates a file that is stored in S3 and a link is emailed to the client.
 */
export const DownloadButton = ({
  className,
  fileName,
  gaEventConfig,
  headers,
  isDisabled,
  onClick,
  text,
  trackingId,
  ...buttonProps
}: Props) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async () => {
    setIsDownloading(true);

    if (isDefined(gaEventConfig)) {
      recordEvent(gaEventConfig.eventCategory, gaEventConfig.eventAction);
    }

    const blob = await onClick();
    if (blob) {
      saveBlob(blob, fileName ?? 'download', headers ?? {});
    }

    setIsDownloading(false);
  };

  return (
    <DefaultIconButton
      className={className}
      iconName={isDownloading ? 'spinner' : 'download'}
      isDisabled={isDownloading || isDisabled}
      onClick={() => handleClick()}
      trackingId={trackingId}
      {...(isDisabled ? {} : { iconColor: theme.colors.gray8 })}
      {...buttonProps}
    >
      {text}
    </DefaultIconButton>
  );
};
